import React from "react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";

const CurrencyInput = ({ label, value, onChange, disabled, className, isReadOnly, isNegative }) => (
    <div className={`space-y-1.5 ${className || ""} min-w-0`}>
        <Label className={`text-xs font-medium whitespace-nowrap ${isNegative ? "text-red-500" : "text-muted-foreground"}`}>{label}</Label>
        <div className="relative">
            <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold ${isNegative ? "text-red-400" : "text-muted-foreground"}`}>R$</span>
            <Input
                type="number"
                step="0.01"
                value={value}
                onChange={onChange}
                disabled={disabled}
                readOnly={isReadOnly}
                className={`pl-8 bg-card focus:bg-white dark:focus:bg-input transition-colors ${isReadOnly ? "bg-muted border-statusInfo/10" : isNegative ? "border-statusError/20 focus:border-statusError/30" : "border-border"}`}
            />
        </div>
    </div>
);

export function DealForm({ data, onChange, disabled, showTipoAceite = false, temValores = true, camposValores = [], parcelado = true, calculoAutomatico = true, tipoAceitePadrao = null }) {
    const isCessao = tipoAceitePadrao === "cessao";
    const hasExtras = camposValores.length > 0;

    const handleChange = (field, value) => {
        onChange({ ...data, [field]: value });
    };

    const handleExtrasChange = (key, value) => {
        const updated = { ...(data.valores_extras || {}), [key]: value };
        const newData = { ...data, valores_extras: updated };

        if (calculoAutomatico && data.valor_credito) {
            const credito = parseFloat(data.valor_credito) || 0;
            const fgts = parseFloat(data.valor_fgts) || 0;
            const desagio = parseFloat(data.percentual_desagio) || 0;
            const sindicato = parseFloat(data.valor_sindicato) || 0;
            const contador = parseFloat(data.valor_contador) || 0;
            const extraDeductions = camposValores
                .filter(c => c.negativo)
                .reduce((acc, campo) => {
                    const v = parseFloat(updated[campo.key] || 0);
                    return acc + (isNaN(v) ? 0 : v);
                }, 0);
            const creditoSemFgts = credito - fgts;
            const propostaValue = creditoSemFgts > 0
                ? Math.max(0, (creditoSemFgts * (1 - desagio / 100)) - sindicato - contador - extraDeductions)
                : 0;
            newData.valor_proposta_final = propostaValue > 0 ? propostaValue.toFixed(2) : "";
        }

        onChange(newData);
    };

    return (
        <div className="space-y-6">
            {/* Status Override (edit mode only) */}
            {showTipoAceite && (
                <div className={`p-4 rounded-xl border space-y-2 ${isCessao ? "bg-statusSuccessBg/60 border-statusSuccess/20" : "bg-statusWarningBg/60 border-amber-100"}`}>
                    <h5 className={`text-[10px] font-bold uppercase tracking-wider ${isCessao ? "text-emerald-600" : "text-amber-500"}`}>
                        {isCessao ? "Tipo de Aceite — Processo de Cessão" : "Status de Assinatura"}
                    </h5>
                    <div className="space-y-1.5">
                        <Label className="text-xs font-medium text-muted-foreground">
                            {isCessao ? "O substituído aceitou qual modalidade?" : "Status"}
                        </Label>
                        <Select
                            value={data.tipo_aceite ?? "pendente"}
                            onValueChange={val => handleChange('tipo_aceite', val === "pendente" ? null : val)}
                            disabled={disabled}
                        >
                            <SelectTrigger className={`bg-white dark:bg-input ${isCessao ? "border-statusSuccess/20 text-statusSuccess font-medium" : "border-border"}`}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="pendente">Pendente</SelectItem>
                                <SelectItem value="acordo">Aceitou o Acordo</SelectItem>
                                <SelectItem value="cessao">Aceitou a Cessão</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            )}

            {!temValores ? (
                <div className="flex items-center gap-2 px-4 py-3 bg-muted border border-border rounded-lg text-sm text-muted-foreground">
                    <span className="text-slate-300">—</span>
                    Este processo não possui valores financeiros.
                </div>
            ) : (
                <>
                    {/* Custom value fields — campos personalizados independentes de valor_credito */}
                    {hasExtras && (
                        <div className="bg-indigo-50/40 p-4 rounded-xl border border-indigo-100 space-y-4">
                            <h5 className="text-[10px] font-bold uppercase tracking-wider text-indigo-400">Valores Personalizados</h5>
                            <div className="grid grid-cols-2 gap-4">
                                {camposValores.map(campo => (
                                    <CurrencyInput
                                        key={campo.key}
                                        label={campo.negativo ? `(−) ${campo.label}` : campo.label}
                                        value={(data.valores_extras || {})[campo.key] ?? ""}
                                        onChange={e => handleExtrasChange(campo.key, e.target.value)}
                                        disabled={disabled}
                                        className={campo.negativo ? "negative-campo" : ""}
                                        isNegative={campo.negativo}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Core Inputs Section */}
                    <div className="bg-muted/50 p-4 rounded-xl border border-border space-y-4">
                        <h5 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Dados de Origem e Deságio</h5>
                        <div className="grid grid-cols-2 gap-4">
                            <CurrencyInput
                                label="Valor Crédito"
                                value={data.valor_credito}
                                onChange={e => handleChange('valor_credito', e.target.value)}
                                disabled={disabled}
                            />
                            <div className="space-y-1.5 min-w-0">
                                <Label className="text-xs font-medium text-muted-foreground whitespace-nowrap">% Deságio</Label>
                                <div className="relative">
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-muted-foreground">%</span>
                                    <Input
                                        type="number"
                                        value={data.percentual_desagio}
                                        onChange={e => handleChange('percentual_desagio', e.target.value)}
                                        disabled={disabled}
                                        className="pr-8 bg-card border-border focus:bg-white dark:focus:bg-input transition-colors"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <CurrencyInput
                                label="Honorários Sindicato"
                                value={data.valor_sindicato}
                                onChange={e => handleChange('valor_sindicato', e.target.value)}
                                disabled={disabled}
                            />
                            <CurrencyInput
                                label="Honorários Contador"
                                value={data.valor_contador}
                                onChange={e => handleChange('valor_contador', e.target.value)}
                                disabled={disabled}
                            />
                        </div>

                    </div>

                    {/* Payment Configuration — only when parcelado */}
                    {parcelado && (
                        <div className="space-y-4">
                            <h5 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Configuração de Pagamento</h5>
                            <div className="grid grid-cols-3 gap-6">
                                <div className="space-y-1.5 min-w-0">
                                    <Label className="text-xs font-medium text-muted-foreground whitespace-nowrap">Nº Parcelas</Label>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={data.parcelas}
                                        onChange={e => handleChange('parcelas', e.target.value)}
                                        disabled={disabled}
                                        className="bg-card border-border focus:bg-white dark:focus:bg-input transition-colors w-full"
                                    />
                                </div>
                                <div className="space-y-1.5 min-w-0">
                                    <Label className="text-xs font-medium text-muted-foreground whitespace-nowrap">Data 1ª Parcela</Label>
                                    <Input
                                        type="date"
                                        value={data.data_primeira_parcela}
                                        onChange={e => handleChange('data_primeira_parcela', e.target.value)}
                                        disabled={disabled}
                                        className="bg-card border-border focus:bg-white transition-colors text-xs w-full"
                                    />
                                </div>
                                <div className="space-y-1.5 min-w-0">
                                    <Label className="text-xs font-medium text-muted-foreground whitespace-nowrap">Data Última Parcela</Label>
                                    <Input
                                        type="date"
                                        value={data.data_ultima_parcela}
                                        onChange={e => handleChange('data_ultima_parcela', e.target.value)}
                                        disabled={disabled}
                                        className="bg-card border-border focus:bg-white transition-colors text-xs w-full"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Period Values — only when parcelado */}
                    {parcelado && (
                        <div className="bg-muted p-4 rounded-xl border border-border space-y-4">
                            <h5 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Parcelas por Período</h5>
                            <div className="grid grid-cols-3 gap-3">
                                <CurrencyInput
                                    label="1º Período"
                                    value={data.valor_parcela_primeiro_periodo}
                                    onChange={e => handleChange('valor_parcela_primeiro_periodo', e.target.value)}
                                    disabled={disabled}
                                />
                                <CurrencyInput
                                    label="2º Período"
                                    value={data.valor_parcela_segundo_periodo}
                                    onChange={e => handleChange('valor_parcela_segundo_periodo', e.target.value)}
                                    disabled={disabled}
                                />
                                <CurrencyInput
                                    label="3º Período"
                                    value={data.valor_parcela_terceiro_periodo}
                                    onChange={e => handleChange('valor_parcela_terceiro_periodo', e.target.value)}
                                    disabled={disabled}
                                />
                            </div>
                        </div>
                    )}

                    {/* Proposal summary — read-only when auto-calc, editable when manual */}
                    <div className={`p-4 rounded-xl border space-y-4 ${calculoAutomatico ? "bg-statusInfoBg/50 border-statusInfo/10" : "bg-orange-50/40 border-orange-100"}`}>
                        <div className="flex justify-between items-center">
                            <h5 className={`text-[10px] font-bold uppercase tracking-wider ${calculoAutomatico ? "text-blue-400" : "text-orange-500"}`}>
                                {isCessao ? "Valores da Cessão" : "Valores da Proposta"}
                            </h5>
                            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${calculoAutomatico ? "text-blue-500 bg-statusInfoBg" : "text-orange-600 bg-orange-100"}`}>
                                {calculoAutomatico ? "Calculado automaticamente" : "Preenchimento manual"}
                            </span>
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                            <CurrencyInput
                                label={isCessao ? "Valor Líquido ao Cedente" : "Valor da Proposta Final"}
                                value={data.valor_proposta_final}
                                onChange={e => handleChange('valor_proposta_final', e.target.value)}
                                disabled={disabled}
                                isReadOnly={calculoAutomatico}
                            />
                        </div>
                        {parcelado && (
                            <div className={`grid grid-cols-2 gap-4 border-t pt-4 ${calculoAutomatico ? "border-statusInfo/10" : "border-orange-100"}`}>
                                <CurrencyInput
                                    label="Valor Parcelado Total"
                                    value={data.valor_parcelado}
                                    onChange={e => handleChange('valor_parcelado', e.target.value)}
                                    disabled={disabled}
                                    isReadOnly={calculoAutomatico}
                                />
                                <CurrencyInput
                                    label="Valor de Cada Parcela"
                                    value={data.valor_parcela}
                                    onChange={e => handleChange('valor_parcela', e.target.value)}
                                    disabled={disabled}
                                    isReadOnly={calculoAutomatico}
                                />
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
